"use client";

import { ArrowUpIcon } from "@heroicons/react/24/outline";
import { StopIcon } from "@heroicons/react/24/solid";
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Oriented } from "@/data/constants/oriented";
import { useAppSelector } from "@/hooks/redux-hooks";
import { useLang } from "@/hooks/use-lang";
import { isJSON } from "@/utils/is-json";

import { startNewChat } from "../../helpers/operations/start-new-chat";
import { useChat } from "../../hooks/use-chat";
import { Disclaimer } from "./Disclaimer";
import { InvestStyleDialogToggle } from "./InvestStyleDialogToggle";
import { InvestStyleSelect } from "./InvestStyleSelect";

export const ChatBottomBar = () => {
  const { lang, t } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  const pathname = usePathname();
  const [investStyle, setInvestStyle] = useState<Oriented | null>(null);
  const { handleSubmit, isLoading } = useChat();
  const isStreaming = useAppSelector((state) => state.chat.isStreaming);
  const disableSubmit = isStreaming || isLoading;

  useEffect(() => {
    const storage = localStorage.getItem("investStyle");
    const investStyle = storage ? JSON.parse(storage) : null;
    setInvestStyle(investStyle);
  }, []);

  useEffect(() => {
    const handleChangeInvestStyle = (e: Event) => {
      const customEvent = e as CustomEvent;
      const investStyle = isJSON(customEvent.detail)
        ? JSON.parse(customEvent.detail)
        : customEvent.detail;
      setInvestStyle(investStyle);
    };

    if (typeof window !== "undefined")
      window.addEventListener("changeInvestStyle", handleChangeInvestStyle);

    return () => {
      if (typeof window !== "undefined")
        window.removeEventListener(
          "changeInvestStyle",
          handleChangeInvestStyle
        );
    };
  }, []);

  const handleSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const query = inputRef.current?.value ?? "";
    if (inputRef.current?.value) {
      inputRef.current.value = "";
    }

    const sessionId = pathname
      .match(/\/winwingpt\/gpt-session-([\w-]+)$/)?.[0]
      .split("/")
      .pop();

    handleSubmit({ query, investStyle, sessionId });
  };

  return (
    <form
      onSubmit={handleSubmitForm}
      className="relative mt-auto space-y-2 pt-2"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-2">
        <div className="flex items-center gap-2">
          <InvestStyleSelect />
          <InvestStyleDialogToggle />
        </div>
        <p
          onClick={() => startNewChat({ lang })}
          className="hidden cursor-pointer pl-2 text-xs font-semibold text-sky-600 @3xl:block"
        >
          {t.winwingpt.backToMain}
        </p>
      </div>
      <div className="flex items-center gap-x-2">
        <input
          type="text"
          ref={inputRef}
          aria-label="input question"
          disabled={disableSubmit}
          placeholder={
            disableSubmit ? t.winwingpt.streaming : t.winwingpt.inputQuestion
          }
          className={twMerge(
            "w-full rounded-full border-zinc-300 pl-5 text-sm outline-none",
            disableSubmit &&
              "pointer-events-none animate-pulse cursor-not-allowed"
          )}
        />
        <button
          type="submit"
          aria-label="submit question"
          disabled={disableSubmit}
          className={twMerge(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-500 transition-colors hover:bg-sky-400",
            disableSubmit &&
              "pointer-events-none animate-pulse cursor-not-allowed bg-zinc-400 hover:bg-zinc-400"
          )}
        >
          {disableSubmit ? (
            <StopIcon className="h-5 w-5 text-white" />
          ) : (
            <ArrowUpIcon className="h-5 w-5 text-white" />
          )}
        </button>
      </div>
      <Disclaimer lang={lang} />
    </form>
  );
};
